// components
import { Chip, Tab, Tooltip, useMediaQuery } from '@mui/material';
import { Icon } from '@sdb/planning-ui';
import {
  StyledBoxAutoMarginLeft,
  StyledBoxFlexCenter,
  StyledBoxFlexCenterWrapFullWidth,
  StyledBoxFlexJustifyFullWidth
} from 'components/shared/Box/Box.styles';
import { IconContainer } from 'components/shared/Header/Header.styles';
import { StyledTypographyBodyBold } from 'components/shared/Typography/Typography.styles';
import { StyledButtonIcon } from 'components/employee-portal/notes/Notes.styles';
import {
  StyledCompactChip,
  StyledDrawerTabs,
  StyledTabsActionContainer,
  StyledTonalIconButton
} from './MatchDrawerTables.styles';

// types
import { MatchDrawerTabsProps } from './MatchDrawerTabs.types';

// utils
import { FLEX_DRAWER_FILTER_DEFAULT_VALUES } from './MatchDrawerTables.consts';

export const MatchDrawerTabs = ({
  filters,
  setFilters,
  filterSteps,
  currentStepIndex,
  gotoPrevStep,
  gotoNextStep,
  methods,
  handleFilterClick,
  tab,
  candidates,
  employees,
  handleTabChange,
  handlePreferredEmployees,
  selectedRow,
  activeFilterLabel,
  t,
  tCommon
}: MatchDrawerTabsProps) => {
  const isSmallDevice = useMediaQuery((theme) =>
    theme.breakpoints.down('expanded')
  );
  const isLargeDevice = useMediaQuery((theme) =>
    theme.breakpoints.between('expanded', 'large')
  );

  const AMOUNT_OF_CHIPS_SMALL_DEVICE = 0;
  const AMOUNT_OF_CHIPS_LARGE_DEVICE = 1;

  return (
    <StyledDrawerTabs value={tab} onChange={handleTabChange}>
      <Tab
        value="pendingRequests"
        label={`${t('newFlexRequestDrawer.pendingRequests')} ${
          !!candidates ? `(${candidates.length})` : ''
        }`}
      />
      <Tab
        value="openToQueryAndLink"
        label={`${t('newFlexRequestDrawer.openToQueryAndLink')} ${
          !!employees ? `(${employees.length})` : ''
        }`}
      />
      {tab === 'pendingRequests' && handlePreferredEmployees && (
        <StyledBoxFlexJustifyFullWidth>
          <StyledButtonIcon
            variant="tonal"
            onClick={() => handlePreferredEmployees(selectedRow)}
          >
            <IconContainer>
              <Icon type="star-outline" />
            </IconContainer>
            {tCommon('preferredEmployees')}
          </StyledButtonIcon>
        </StyledBoxFlexJustifyFullWidth>
      )}

      {tab === 'openToQueryAndLink' && (
        <StyledTabsActionContainer>
          {filterSteps.length > 0 && (
            <StyledBoxFlexCenter>
              <StyledTonalIconButton
                type="chevron-left"
                size="small"
                disabled={currentStepIndex === 0}
                onClick={gotoPrevStep}
              />
              <StyledTonalIconButton
                type="chevron-right"
                size="small"
                disabled={currentStepIndex === filterSteps.length - 1}
                onClick={gotoNextStep}
              />
              <StyledTypographyBodyBold>
                {activeFilterLabel}
              </StyledTypographyBodyBold>
              {!isSmallDevice && (
                <StyledBoxFlexCenterWrapFullWidth>
                  {Object.entries(filters).map(([key, value], index) => {
                    if (
                      value &&
                      key !== 'stepName' &&
                      index <=
                        (isLargeDevice
                          ? AMOUNT_OF_CHIPS_SMALL_DEVICE - 1
                          : AMOUNT_OF_CHIPS_LARGE_DEVICE)
                    ) {
                      return (
                        <Chip
                          key={key}
                          size="small"
                          label={
                            typeof value === 'number'
                              ? `${t(key)}: ${value}`
                              : t(key)
                          }
                          onDelete={() => {
                            const newFilters = { ...filters };
                            delete newFilters[key];
                            setFilters(newFilters);
                            methods.reset({
                              ...FLEX_DRAWER_FILTER_DEFAULT_VALUES,
                              ...newFilters
                            });
                          }}
                          disabled={key === 'employeeFlex'}
                        />
                      );
                    }
                  })}
                  {Object.entries(filters).length >
                    (isLargeDevice
                      ? AMOUNT_OF_CHIPS_SMALL_DEVICE - 1
                      : AMOUNT_OF_CHIPS_LARGE_DEVICE + 1) && (
                    <Tooltip
                      title={Object.entries(filters).map(
                        ([key, value], index) => {
                          if (
                            value &&
                            key !== 'stepName' &&
                            index >
                              (isLargeDevice
                                ? AMOUNT_OF_CHIPS_SMALL_DEVICE - 1
                                : AMOUNT_OF_CHIPS_LARGE_DEVICE)
                          )
                            return (
                              <Chip
                                key={key}
                                size="small"
                                label={
                                  typeof value === 'number'
                                    ? `${t(key)}: ${value}`
                                    : t(key)
                                }
                                onDelete={() => {
                                  const newFilters = { ...filters };
                                  delete newFilters[key];
                                  setFilters(newFilters);
                                  methods.reset({
                                    ...FLEX_DRAWER_FILTER_DEFAULT_VALUES,
                                    ...newFilters
                                  });
                                }}
                                disabled={key === 'employeeFlex'}
                              />
                            );
                        }
                      )}
                      slotProps={{
                        popper: { sx: { maxWidth: 440 } },
                        tooltip: {
                          sx: {
                            padding: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1
                          }
                        }
                      }}
                      arrow
                    >
                      <StyledCompactChip label="..." size="small" />
                    </Tooltip>
                  )}
                </StyledBoxFlexCenterWrapFullWidth>
              )}
            </StyledBoxFlexCenter>
          )}
          <StyledBoxAutoMarginLeft>
            <StyledButtonIcon
              id="filter-drawer-popover"
              variant="tonal"
              onClick={handleFilterClick}
            >
              <IconContainer>
                <Icon type="sliders" />
              </IconContainer>
              {t('matchSteps')}
            </StyledButtonIcon>
          </StyledBoxAutoMarginLeft>
        </StyledTabsActionContainer>
      )}
    </StyledDrawerTabs>
  );
};
